/**
 * Pure DOM construction for the chat widget.
 *
 * Each `build*` function returns a fully-formed sub-tree plus refs to the
 * inner nodes the orchestrator (initChat) wires events to. No side effects
 * here other than calling `injectStyles()` once per page.
 *
 * Why split this out from index.ts?
 *   - The DOM layer doesn't depend on the NexorClient or sessionId — keeping
 *     it pure makes the widget easy to reason about and easy to dry-run in
 *     unit tests by mocking `document`.
 *   - The orchestrator becomes a 150-line state machine instead of a
 *     600-line god-function.
 */
import {
  needsCapture,
  FIELD_MAX_LENGTH,
  MESSAGE_MAX_LENGTH,
  getChatDefaultCopy,
  type ResolvedCfg,
  type ResolvedChannel,
  type ChatChannelKey,
} from "./config";
import {
  COUNTRIES,
  countryByIso,
  maxNsn,
  resolveDefaultCountry,
  type Country,
} from "./countries";
import { widgetCss } from "./styles";
import { tokenizeLinks } from "./linkify";
import type { ChatMedia } from "../types";

const STYLE_ATTR = "data-nexor-chat-styles";

// ─── Stylesheet (singleton per page) ───────────────────────────────────────

/**
 * Inject (or refresh) the widget's stylesheet — one shared tag per page. The
 * LAST mount's accent wins, so destroy()+initChat() with a new accentColor
 * re-paints (live theme editors). Two widgets with different accents on one
 * page is still an unsupported use case.
 */
export function injectStyles(
  accent: string,
  accentText: string,
  shape: "rounded" | "square" = "rounded",
): void {
  let style = document.querySelector(`style[${STYLE_ATTR}]`) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.setAttribute(STYLE_ATTR, "1");
    document.head.appendChild(style);
  }
  style.textContent = widgetCss(accent, accentText, shape);
}

// ─── Launcher ──────────────────────────────────────────────────────────────

export interface ChannelButtonRef {
  key: ChatChannelKey;
  /** The clickable element — an <a> for links, a <button> for JS actions. */
  el: HTMLAnchorElement | HTMLButtonElement;
  /** "contact" → opens the contact-request form; undefined → chat/navigation. */
  action?: "contact";
}

export interface LauncherRefs {
  /** The outer container — also the hover target for the speed-dial fan. */
  wrap: HTMLDivElement;
  /** The main bubble button. */
  el: HTMLButtonElement;
  badge: HTMLSpanElement;
  /** The fan container (null when no channels are configured). */
  fan: HTMLDivElement | null;
  /** Resolved fan buttons, in render order. Empty when no fan. */
  channelButtons: ChannelButtonRef[];
}

// Speech-bubble icon — SVG rather than emoji for crisp rendering at any DPR.
const CHAT_ICON = `
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M4 5.5C4 4.67 4.67 4 5.5 4h13c.83 0 1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5H9l-4 4V5.5Z"
          stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
  </svg>`;

const CHANNEL_ICONS: Record<ChatChannelKey, string> = {
  whatsapp: `
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.82 9.82 0 0 0 1.5 5.245l-.999 3.648 3.738-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
    </svg>`,
  call: `
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.5 21 3 13.5 3 4.5c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.4 0 .8-.2 1L6.6 10.8Z"/>
    </svg>`,
  email: `
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
      <path d="m4 7 8 6 8-6" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
    </svg>`,
  instagram: `
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" stroke="currentColor" stroke-width="1.8"/>
      <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.8"/>
      <circle cx="17.2" cy="6.8" r="1.2" fill="currentColor"/>
    </svg>`,
  chat: CHAT_ICON,
};

/**
 * Build the launcher. When `cfg.channels` is non-empty the launcher becomes a
 * speed-dial that fans out channel buttons (revealed on hover / tap). Otherwise
 * it's the classic single bubble that opens the chat on click.
 */
export function buildLauncher(cfg: ResolvedCfg): LauncherRefs {
  const copy = getChatDefaultCopy(cfg.locale);
  const wrap = document.createElement("div");
  wrap.className = "nexor-chat__launcher-wrap";

  const hasFan = cfg.channels.length > 0;

  const { fan, channelButtons } = hasFan
    ? buildChannelFan(cfg.channels)
    : { fan: null, channelButtons: [] as ChannelButtonRef[] };
  if (fan) wrap.appendChild(fan);

  const el = document.createElement("button");
  el.type = "button";
  el.className = "nexor-chat__launcher";
  // With a fan, the bubble toggles the channel menu; without it, it opens chat.
  el.setAttribute(
    "aria-label",
    hasFan ? copy.launcherContactLabel : copy.launcherOpenLabel,
  );
  if (hasFan) {
    el.setAttribute("aria-haspopup", "true");
    el.setAttribute("aria-expanded", "false");
  }
  el.innerHTML = CHAT_ICON;

  const badge = document.createElement("span");
  badge.className = "nexor-chat__unread";
  badge.style.display = "none";
  el.appendChild(badge);
  wrap.appendChild(el);

  return { wrap, el, badge, fan, channelButtons };
}

function buildChannelFan(channels: ResolvedChannel[]): {
  fan: HTMLDivElement;
  channelButtons: ChannelButtonRef[];
} {
  const fan = document.createElement("div");
  fan.className = "nexor-chat__channels";
  fan.setAttribute("role", "menu");

  const channelButtons: ChannelButtonRef[] = [];
  for (const ch of channels) {
    const item = document.createElement("div");
    item.className = "nexor-chat__channel-item";

    const labelEl = document.createElement("span");
    labelEl.className = "nexor-chat__channel-label";
    labelEl.textContent = ch.label;

    let el: HTMLAnchorElement | HTMLButtonElement;
    if (ch.href === null) {
      // The chat action is wired up in JS (opens the panel), not a navigation.
      const btn = document.createElement("button");
      btn.type = "button";
      el = btn;
    } else {
      const a = document.createElement("a");
      a.href = ch.href;
      if (ch.external) {
        a.target = "_blank";
        a.rel = "noopener noreferrer";
      }
      el = a;
    }
    el.className = `nexor-chat__channel nexor-chat__channel--${ch.key}`;
    el.setAttribute("role", "menuitem");
    el.setAttribute("aria-label", ch.label);
    el.innerHTML = CHANNEL_ICONS[ch.key];

    // Label sits to the side (left of a right-anchored widget, right of a
    // left-anchored one — handled in CSS). Both share the item row.
    item.appendChild(labelEl);
    item.appendChild(el);
    fan.appendChild(item);
    channelButtons.push({ key: ch.key, el, action: ch.action });
  }
  return { fan, channelButtons };
}

// ─── Panel ─────────────────────────────────────────────────────────────────

type CaptureField = "first_name" | "last_name" | "email" | "phone";

export interface PanelRefs {
  el: HTMLDivElement;
  closeBtn: HTMLButtonElement;
  restartBtn: HTMLButtonElement | null;
  backBtn: HTMLButtonElement;
  header: HTMLDivElement;
  headerAvatar: HTMLDivElement;
  headerCaption: HTMLParagraphElement;
  /** Header title + subtitle — re-contextualized when the contact form opens. */
  headerTitle: HTMLParagraphElement;
  headerSubtitle: HTMLParagraphElement;
  headerSubtitleText: HTMLSpanElement;
  body: HTMLDivElement;
  form: HTMLFormElement;
  input: HTMLTextAreaElement;
  send: HTMLButtonElement;
  /** Full-panel capture screen (null when this widget never captures). */
  captureView: HTMLDivElement | null;
  captureForm: HTMLFormElement | null;
  captureError: HTMLParagraphElement | null;
  /** Country <select> for the phone field (flag + dial code), null if no phone. */
  phoneCountry: HTMLSelectElement | null;
  captureInputs: Partial<Record<CaptureField, HTMLInputElement>>;
  /** Contact-request view (null when requestContact isn't configured). */
  contact: ContactViewRefs | null;
  /** Intercom-style landing screen (null when the widget never shows one — see buildHomeView). */
  home: HomeViewRefs | null;
}

export interface HomeViewRefs {
  view: HTMLDivElement;
  closeBtn: HTMLButtonElement;
  avatar: HTMLDivElement;
  greetLine: HTMLParagraphElement;
  titleLine: HTMLParagraphElement;
  messageCard: HTMLButtonElement;
  messageTitle: HTMLParagraphElement;
  messageSubtitle: HTMLParagraphElement;
  cards: HTMLDivElement;
  /** The suggestions card, when any suggestions are configured — swappable via
   *  `renderHomeSuggestions()` when the server theme supplies them late. */
  suggestionsCard: HTMLDivElement | null;
  suggestionButtons: HTMLButtonElement[];
}

/** Refs for the in-widget contact-request form + its success state. */
export interface ContactViewRefs {
  view: HTMLDivElement;
  form: HTMLFormElement;
  inputs: Partial<Record<CaptureField, HTMLInputElement>>;
  error: HTMLParagraphElement;
  phoneCountry: HTMLSelectElement | null;
  submitBtn: HTMLButtonElement;
  /** Success panel (hidden until a request succeeds). */
  success: HTMLDivElement;
  successText: HTMLParagraphElement;
  successBtn: HTMLButtonElement;
}

export function buildPanel(cfg: ResolvedCfg): PanelRefs {
  const el = document.createElement("div");
  el.className = "nexor-chat__panel";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-label", cfg.title);

  const { header, closeBtn, restartBtn, backBtn, avatar: headerAvatar, captionEl: headerCaption, titleEl, subtitleEl, subtitleTextEl } =
    buildHeader(cfg);
  const body = buildBody();
  const { form, input, send } = buildComposer(cfg);
  const footer = cfg.showBranding ? buildFooter(cfg) : null;
  const home = buildHomeView(cfg);

  // The capture form is its OWN view (a full-panel screen), not a card mixed in
  // with the messages. `data-view` toggles between the capture screen and the
  // chat (body + composer); see styles.ts.
  const captureInputs: Partial<Record<CaptureField, HTMLInputElement>> = {};
  let captureView: HTMLDivElement | null = null;
  let captureForm: HTMLFormElement | null = null;
  let captureError: HTMLParagraphElement | null = null;
  let phoneCountry: HTMLSelectElement | null = null;
  if (needsCapture(cfg)) {
    const built = buildCaptureView(cfg, captureInputs);
    captureView = built.view;
    captureForm = built.form;
    captureError = built.error;
    phoneCountry = built.phoneCountry;
  }

  // Contact-request view: an alternate full-panel screen opened when a visitor
  // taps a "call me" / "email me" fan button. Built once; its copy is set per
  // channel at click time.
  const contact: ContactViewRefs | null = cfg.requestContact
    ? buildContactView(cfg)
    : null;

  el.setAttribute("data-view", captureView ? "capture" : "chat");

  el.appendChild(header);
  el.appendChild(home.view);
  if (captureView) el.appendChild(captureView);
  if (contact) el.appendChild(contact.view);
  el.appendChild(body);
  el.appendChild(form);
  if (footer) el.appendChild(footer);

  return {
    el,
    closeBtn,
    restartBtn,
    backBtn,
    header,
    headerAvatar,
    headerCaption,
    headerTitle: titleEl,
    headerSubtitle: subtitleEl,
    headerSubtitleText: subtitleTextEl,
    body,
    form,
    input,
    send,
    captureView,
    captureForm,
    captureError,
    phoneCountry,
    captureInputs,
    contact,
    home,
  };
}

// ─── Home view (Intercom-style landing) ─────────────────────────────────────

const SEND_ICON = `
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M4 5.5C4 4.67 4.67 4 5.5 4h13c.83 0 1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5H9l-4 4V5.5Z"
          stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
  </svg>`;

const CHEVRON_ICON = `
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

/**
 * The Home landing screen: a gradient hero (avatar + two-line headline) with
 * white cards floating on top — a "send us a message" card and, when
 * configured, a list of tappable suggested questions. Shown FIRST on a fresh
 * visit (no restored history); a returning visitor skips straight to "chat"
 * (wired in index.ts).
 */
function buildHomeView(cfg: ResolvedCfg): HomeViewRefs {
  const view = document.createElement("div");
  view.className = "nexor-chat__home";

  const hero = document.createElement("div");
  hero.className = "nexor-chat__home-hero";

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "nexor-chat__home-close";
  closeBtn.setAttribute("aria-label", cfg.aria.close);
  closeBtn.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>`;
  hero.appendChild(closeBtn);

  const avatar = document.createElement("div");
  avatar.className = "nexor-chat__home-avatar";
  avatar.setAttribute("aria-hidden", "true");
  renderAvatar(avatar, cfg.avatarUrl, cfg.title);
  hero.appendChild(avatar);

  const greetLine = document.createElement("p");
  greetLine.className = "nexor-chat__home-greeting";
  greetLine.textContent = cfg.homeTitle[0];
  hero.appendChild(greetLine);

  const titleLine = document.createElement("p");
  titleLine.className = "nexor-chat__home-title";
  titleLine.textContent = cfg.homeTitle[1];
  hero.appendChild(titleLine);

  view.appendChild(hero);

  const cards = document.createElement("div");
  cards.className = "nexor-chat__home-cards";

  const messageCard = document.createElement("button");
  messageCard.type = "button";
  messageCard.className = "nexor-chat__home-card";
  const messageRow = document.createElement("div");
  messageRow.className = "nexor-chat__home-message";
  const messageCopy = document.createElement("div");
  messageCopy.className = "nexor-chat__home-message-copy";
  const messageTitle = document.createElement("p");
  messageTitle.className = "nexor-chat__home-message-title";
  messageTitle.textContent = cfg.homeCardTitle;
  const messageSubtitle = document.createElement("p");
  messageSubtitle.className = "nexor-chat__home-message-subtitle";
  messageSubtitle.textContent = cfg.subtitle || "";
  messageCopy.appendChild(messageTitle);
  messageCopy.appendChild(messageSubtitle);
  const messageIcon = document.createElement("div");
  messageIcon.className = "nexor-chat__home-message-icon";
  messageIcon.innerHTML = SEND_ICON;
  messageRow.appendChild(messageCopy);
  messageRow.appendChild(messageIcon);
  messageCard.appendChild(messageRow);
  cards.appendChild(messageCard);

  const built = buildHomeSuggestionsCard(cfg.suggestions);
  if (built) cards.appendChild(built.card);

  view.appendChild(cards);

  return {
    view,
    closeBtn,
    avatar,
    greetLine,
    titleLine,
    messageCard,
    messageTitle,
    messageSubtitle,
    cards,
    suggestionsCard: built?.card ?? null,
    suggestionButtons: built?.buttons ?? [],
  };
}

/**
 * The Home suggestions card (a list of tappable question rows). Extracted so
 * the orchestrator can rebuild it later — e.g. when the server theme supplies
 * suggestions that weren't known at mount time. Returns null when `suggestions`
 * is empty (no card to show).
 */
export function buildHomeSuggestionsCard(
  suggestions: string[],
): { card: HTMLDivElement; buttons: HTMLButtonElement[] } | null {
  if (!suggestions.length) return null;
  const card = document.createElement("div");
  card.className = "nexor-chat__home-card nexor-chat__home-suggestions";
  const buttons: HTMLButtonElement[] = [];
  for (const text of suggestions) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "nexor-chat__home-suggestion-row";
    const label = document.createElement("span");
    label.className = "nexor-chat__home-suggestion-text";
    label.textContent = text;
    row.appendChild(label);
    row.insertAdjacentHTML("beforeend", CHEVRON_ICON);
    card.appendChild(row);
    buttons.push(row);
  }
  return { card, buttons };
}

/**
 * Render an avatar container: the client logo when a URL is provided, else
 * initials derived from `initialsSource`. Shared by the header avatar and the
 * Home hero avatar, and reused when the server theme supplies `avatar_url`
 * after mount.
 */
export function renderAvatar(
  container: HTMLDivElement,
  avatarUrl: string | undefined,
  initialsSource: string,
): void {
  container.replaceChildren();
  if (avatarUrl) {
    const img = document.createElement("img");
    img.src = avatarUrl;
    img.alt = "";
    container.appendChild(img);
  } else {
    container.textContent = initialsOf(initialsSource);
  }
}

function buildHeader(cfg: ResolvedCfg): {
  header: HTMLDivElement;
  closeBtn: HTMLButtonElement;
  restartBtn: HTMLButtonElement | null;
  backBtn: HTMLButtonElement;
  avatar: HTMLDivElement;
  captionEl: HTMLParagraphElement;
  titleEl: HTMLParagraphElement;
  subtitleEl: HTMLParagraphElement;
  subtitleTextEl: HTMLSpanElement;
} {
  const copy = getChatDefaultCopy(cfg.locale);
  const header = document.createElement("div");
  header.className = "nexor-chat__header";

  // Back-to-home control. Hidden by default (data-show-back on the header
  // toggles it); only relevant once a visitor has seen the Home screen.
  const backBtn = document.createElement("button");
  backBtn.type = "button";
  backBtn.className = "nexor-chat__back";
  backBtn.setAttribute("aria-label", cfg.aria.back);
  backBtn.title = "Back";
  backBtn.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
  header.appendChild(backBtn);

  // Avatar: client logo when provided, else the title's initials. The green
  // presence ring is part of the avatar (CSS box-shadow) — no loose dot.
  const avatar = document.createElement("div");
  avatar.className = "nexor-chat__avatar";
  avatar.setAttribute("aria-hidden", "true");
  renderAvatar(avatar, cfg.avatarUrl, cfg.agentName);
  avatar.setAttribute("data-presence", cfg.presence);
  header.appendChild(avatar);

  // Title + subtitle are built as elements (not innerHTML) so the orchestrator
  // can re-contextualize them — e.g. when the contact-request form is open the
  // header becomes the form's title/explainer instead of the chat title.
  const info = document.createElement("div");

  const titleEl = document.createElement("p");
  titleEl.className = "nexor-chat__title";
  titleEl.textContent = cfg.agentName;
  info.appendChild(titleEl);

  // Bajo el nombre del agente, la empresa — solo visible en la vista chat.
  const captionEl = document.createElement("p");
  captionEl.className = "nexor-chat__agent-caption";
  captionEl.textContent = `${cfg.title} · ${cfg.presenceLabels[cfg.presence]}`;
  info.appendChild(captionEl);

  // The status dot signals "we're live and listening" — purely cosmetic, but a
  // strong UX signal. Hidden from assistive tech (title carries the meaning) and
  // hidden by CSS in the contact-form view (a form isn't "live and listening").
  const subtitleEl = document.createElement("p");
  subtitleEl.className = "nexor-chat__subtitle";
  const dot = document.createElement("span");
  dot.className = "nexor-chat__status-dot";
  dot.setAttribute("aria-hidden", "true");
  const subtitleTextEl = document.createElement("span");
  subtitleTextEl.className = "nexor-chat__subtitle-text";
  subtitleTextEl.textContent = cfg.subtitle ?? "";
  subtitleEl.appendChild(dot);
  subtitleEl.appendChild(subtitleTextEl);
  if (!cfg.subtitle) subtitleEl.hidden = true;
  info.appendChild(subtitleEl);

  header.appendChild(info);

  // Restart: wipes the conversation and starts a brand-new lead. Lives next to
  // close so it's reachable any time the chat is open (not just from the resume
  // banner). Hidden on the capture screen via CSS — nothing to reset yet.
  let restartBtn: HTMLButtonElement | null = null;
  if (cfg.showRestart) {
    restartBtn = document.createElement("button");
    restartBtn.type = "button";
    restartBtn.className = "nexor-chat__restart";
    restartBtn.setAttribute("aria-label", cfg.restartLabel);
    restartBtn.title = cfg.restartLabel;
    restartBtn.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M4 5v5h5M20 19v-5h-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M19 10a7 7 0 0 0-12-3L4 10M5 14a7 7 0 0 0 12 3l3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    header.appendChild(restartBtn);
  }

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "nexor-chat__close";
  closeBtn.setAttribute("aria-label", cfg.aria.close);
  closeBtn.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>
  `;
  header.appendChild(closeBtn);
  return { header, closeBtn, restartBtn, backBtn, avatar, captionEl, titleEl, subtitleEl, subtitleTextEl };
}

function buildBody(): HTMLDivElement {
  const body = document.createElement("div");
  body.className = "nexor-chat__body";
  return body;
}

/** Full-panel capture screen: a centered card that owns the whole view. */
function buildCaptureView(
  cfg: ResolvedCfg,
  inputsOut: Partial<Record<CaptureField, HTMLInputElement>>,
): {
  view: HTMLDivElement;
  form: HTMLFormElement;
  error: HTMLParagraphElement;
  phoneCountry: HTMLSelectElement | null;
} {
  const view = document.createElement("div");
  view.className = "nexor-chat__capture-view";
  const { form, error, phoneCountry } = buildCaptureForm(cfg, inputsOut);
  const hero = document.createElement("p");
  hero.className = "nexor-chat__capture-hero";
  hero.textContent = cfg.capture.label;
  view.appendChild(hero);
  form.querySelector(".nexor-chat__capture-label")?.remove();
  view.appendChild(form);
  return { view, form, error, phoneCountry };
}

/**
 * Rebuild the capture form in place after `capture.fields` changed (the server
 * theme lands well after the panel is built). Mutates `panel.captureInputs` so
 * existing references stay valid; the caller re-wires listeners.
 */
export function rebuildCaptureForm(cfg: ResolvedCfg, panel: PanelRefs): boolean {
  const old = panel.captureForm;
  if (!old) return false;
  for (const key of Object.keys(panel.captureInputs) as CaptureField[]) {
    delete panel.captureInputs[key];
  }
  const built = buildCaptureForm(cfg, panel.captureInputs);
  built.form.querySelector(".nexor-chat__capture-label")?.remove();
  old.replaceWith(built.form);
  panel.captureForm = built.form;
  panel.captureError = built.error;
  panel.phoneCountry = built.phoneCountry;
  return true;
}

function buildCaptureForm(
  cfg: ResolvedCfg,
  inputsOut: Partial<Record<CaptureField, HTMLInputElement>>,
): {
  form: HTMLFormElement;
  error: HTMLParagraphElement;
  phoneCountry: HTMLSelectElement | null;
} {
  const copy = getChatDefaultCopy(cfg.locale);
  const form = document.createElement("form");
  form.className = "nexor-chat__capture";
  form.noValidate = true;
  let phoneCountry: HTMLSelectElement | null = null;

  const label = document.createElement("p");
  label.className = "nexor-chat__capture-label";
  label.textContent = cfg.capture.label;
  form.appendChild(label);

  const fields = cfg.capture.fields;
  const ph = (f: CaptureField) =>
    cfg.capture.placeholders[f] ?? copy.capture.placeholders[f];

  // first_name + last_name share a row to feel like a single "your name" prompt.
  if (fields.includes("first_name") || fields.includes("last_name")) {
    const row = document.createElement("div");
    row.className = "nexor-chat__capture-row";
    if (fields.includes("first_name")) {
      inputsOut.first_name = makeInput("first_name", ph("first_name"), "text", cfg.lead?.first_name);
      row.appendChild(inputsOut.first_name);
    }
    if (fields.includes("last_name")) {
      inputsOut.last_name = makeInput("last_name", ph("last_name"), "text", cfg.lead?.last_name);
      row.appendChild(inputsOut.last_name);
    }
    if (row.childElementCount > 0) form.appendChild(row);
  }
  if (fields.includes("email")) {
    inputsOut.email = makeInput("email", ph("email"), "email", cfg.lead?.email);
    form.appendChild(inputsOut.email);
  }
  if (fields.includes("phone")) {
    const built = buildPhoneField(cfg, ph("phone"));
    inputsOut.phone = built.input;
    phoneCountry = built.select;
    form.appendChild(built.wrap);
  }

  // Inline validation message (hidden until a submit fails). role=alert so
  // assistive tech announces it.
  const error = document.createElement("p");
  error.className = "nexor-chat__capture-error";
  error.setAttribute("role", "alert");
  error.hidden = true;
  form.appendChild(error);

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "nexor-chat__capture-submit";
  submit.textContent = cfg.capture.submitLabel;
  form.appendChild(submit);

  return { form, error, phoneCountry };
}

/** The countries to offer, in order. Filtered to `capture.phoneCountries` (ISO
 *  codes) when provided, else the full table. */
function resolveCountryList(isos: string[]): Country[] {
  if (!isos.length) return COUNTRIES;
  const picked = isos
    .map((i) => countryByIso(i))
    .filter((c): c is Country => Boolean(c));
  return picked.length ? picked : COUNTRIES;
}

/**
 * The phone field: a country <select> (flag + dial code) joined to a digit-only
 * input. The input's maxlength tracks the selected country's number length, so a
 * visitor can't type more digits than the country needs (index.ts also strips
 * non-digits + re-caps on country change).
 */
function buildPhoneField(
  cfg: ResolvedCfg,
  placeholder: string,
): { wrap: HTMLDivElement; select: HTMLSelectElement; input: HTMLInputElement } {
  const copy = getChatDefaultCopy(cfg.locale);
  const wrap = document.createElement("div");
  wrap.className = "nexor-chat__phone";

  const list = resolveCountryList(cfg.capture.phoneCountries);
  const def = resolveDefaultCountry(cfg.capture.phoneCountryCode);

  const select = document.createElement("select");
  select.className = "nexor-chat__phone-country";
  select.name = "phone_country";
  select.setAttribute("aria-label", cfg.aria.country);
  for (const c of list) {
    const opt = document.createElement("option");
    opt.value = c.iso;
    opt.textContent = `${c.flag} ${c.dial}`;
    if (c.iso === def.iso) opt.selected = true;
    select.appendChild(opt);
  }

  const input = document.createElement("input");
  input.name = "phone";
  input.type = "tel";
  input.inputMode = "numeric";
  input.autocomplete = "tel-national";
  input.placeholder = placeholder;
  input.className = "nexor-chat__input nexor-chat__phone-number";
  input.maxLength = maxNsn(def);
  // Prefill from a known lead phone: strip the dial, keep national digits.
  if (cfg.lead?.phone) {
    const digits = String(cfg.lead.phone).replace(/\D/g, "");
    const dialDigits = def.dial.replace(/\D/g, "");
    const national = digits.startsWith(dialDigits) ? digits.slice(dialDigits.length) : digits;
    input.value = national.slice(0, input.maxLength);
  }

  wrap.appendChild(select);
  wrap.appendChild(input);
  return { wrap, select, input };
}

// ─── Contact-request view (call me / email me) ──────────────────────────────

const CONTACT_FIELDS: CaptureField[] = ["first_name", "last_name", "email", "phone"];

/**
 * The contact-request screen: a back control, a channel-specific title +
 * subtitle, the same name/email/phone fields as capture, and a success panel.
 * Title / subtitle / submit / success copy are filled per channel by the
 * orchestrator (index.ts) when the visitor opens the form, since the chosen
 * channel isn't known at build time.
 */
function buildContactView(cfg: ResolvedCfg): ContactViewRefs {
  const copy = getChatDefaultCopy(cfg.locale);
  const rc = cfg.requestContact;
  const view = document.createElement("div");
  view.className = "nexor-chat__contact-view";
  // "form" → fields shown; "sent" → success panel shown (CSS-driven toggle).
  view.setAttribute("data-state", "form");

  // No in-body title/subtitle and no "back" control: the panel HEADER carries
  // the form's title + explainer (set per channel by the orchestrator), so the
  // black header itself reads as a form rather than a chat. The header × closes.
  const form = document.createElement("form");
  form.className = "nexor-chat__capture nexor-chat__contact-form";
  form.noValidate = true;

  const inputs: Partial<Record<CaptureField, HTMLInputElement>> = {};
  const ph = (f: CaptureField) =>
    cfg.capture.placeholders[f] ?? copy.capture.placeholders[f];

  // Always collect first/last name (shared row), email and phone — the visitor
  // gave no info yet, so we ask for everything we'd need to reach them.
  const row = document.createElement("div");
  row.className = "nexor-chat__capture-row";
  inputs.first_name = makeInput("first_name", ph("first_name"), "text", cfg.lead?.first_name);
  inputs.last_name = makeInput("last_name", ph("last_name"), "text", cfg.lead?.last_name);
  row.appendChild(inputs.first_name);
  row.appendChild(inputs.last_name);
  form.appendChild(row);

  inputs.email = makeInput("email", ph("email"), "email", cfg.lead?.email);
  form.appendChild(inputs.email);

  const phone = buildPhoneField(cfg, ph("phone"));
  inputs.phone = phone.input;
  const phoneCountry = phone.select;
  form.appendChild(phone.wrap);

  const error = document.createElement("p");
  error.className = "nexor-chat__capture-error";
  error.setAttribute("role", "alert");
  error.hidden = true;
  form.appendChild(error);

  const submitBtn = document.createElement("button");
  submitBtn.type = "submit";
  submitBtn.className = "nexor-chat__capture-submit";
  form.appendChild(submitBtn);
  view.appendChild(form);

  // Success panel — shown in place of the form once the request lands
  // (visibility driven by the view's data-state, see styles.ts).
  const success = document.createElement("div");
  success.className = "nexor-chat__contact-success";
  const successIcon = document.createElement("div");
  successIcon.className = "nexor-chat__contact-success-icon";
  successIcon.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  const successText = document.createElement("p");
  successText.className = "nexor-chat__contact-success-text";
  const successBtn = document.createElement("button");
  successBtn.type = "button";
  successBtn.className = "nexor-chat__capture-submit";
  successBtn.textContent = rc?.doneLabel ?? copy.contact.doneLabel;
  success.appendChild(successIcon);
  success.appendChild(successText);
  success.appendChild(successBtn);
  view.appendChild(success);

  // CONTACT_FIELDS documents the field set above; referenced to keep the
  // constant "used" for tree-shaking-friendly builds.
  void CONTACT_FIELDS;

  return {
    view,
    form,
    inputs,
    error,
    phoneCountry,
    submitBtn,
    success,
    successText,
    successBtn,
  };
}

function buildComposer(cfg: ResolvedCfg): {
  form: HTMLFormElement;
  input: HTMLTextAreaElement;
  send: HTMLButtonElement;
} {
  const copy = getChatDefaultCopy(cfg.locale);
  const form = document.createElement("form");
  form.className = "nexor-chat__composer";

  // Auto-growing textarea: starts at one line, expands to ~4 lines, then scrolls.
  // Enter sends; Shift+Enter inserts a newline (wired in index.ts).
  const input = document.createElement("textarea");
  input.className = "nexor-chat__input";
  input.placeholder = cfg.inputPlaceholder;
  input.rows = 1;
  input.maxLength = MESSAGE_MAX_LENGTH;
  input.setAttribute("autocomplete", "off");

  const send = document.createElement("button");
  send.type = "submit";
  send.className = "nexor-chat__send";
  send.setAttribute("aria-label", cfg.aria.send);
  // Arrow-up icon (iMessage/ChatGPT style) — pairs with the circular button;
  // language-agnostic and crisper at 18px than the old hand-drawn paper plane.
  send.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 19V5.8M5.8 12 12 5.8 18.2 12"
            stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  const card = document.createElement("div");
  card.className = "nexor-chat__composer-card";
  const actions = document.createElement("div");
  actions.className = "nexor-chat__composer-actions";
  actions.appendChild(send);
  card.appendChild(input);
  card.appendChild(actions);
  // La tarjeta entera ES el input: click en cualquier zona muerta enfoca el
  // textarea (mousedown para no robar el foco si ya esta escribiendo).
  card.addEventListener("mousedown", (e) => {
    if (e.target === card || e.target === actions) {
      e.preventDefault();
      input.focus();
    }
  });
  form.appendChild(card);
  return { form, input, send };
}

// The Nexor wordmark — the SAME mark the site navbar uses (src NexorLogo.jsx),
// inlined as SVG so the bundle stays asset-free. The gradient id is namespaced
// to avoid colliding with a host page that also renders the Nexor logo.
const NEXOR_WORDMARK = `
  <svg viewBox="0 0 1722 505" fill="none" xmlns="http://www.w3.org/2000/svg" class="nexor-chat__brand-logo" role="img" aria-label="Nexor">
    <defs>
      <linearGradient id="nexorChatBrandGrad" x1="1195.44" y1="21.3423" x2="1195.44" y2="504.454" gradientUnits="userSpaceOnUse">
        <stop stop-color="#5EEAD4"/>
        <stop offset="0.55" stop-color="#9CA3AF"/>
        <stop offset="1" stop-color="#FB7185"/>
      </linearGradient>
    </defs>
    <path d="M31.824 416.676V103.332H67.32L82.008 147.396C92.412 125.976 125.46 99.0477 196.452 99.0477C299.268 99.0477 319.464 159.024 319.464 247.764V416.676H254.592V242.256C254.592 186.564 244.8 153.516 178.092 153.516C111.384 153.516 97.308 185.952 97.308 242.256V416.676H31.824ZM506.373 420.96C380.301 420.96 349.089 343.236 349.089 259.392C349.089 175.548 380.913 99.0477 505.761 99.0477C621.429 99.0477 653.865 187.176 645.909 285.708H417.021C420.693 339.564 447.621 370.776 503.925 370.776C555.945 370.776 572.469 345.684 578.589 320.592H647.133C641.625 373.836 604.905 420.96 506.373 420.96ZM417.633 233.688H578.589V232.464C579.201 189.624 559.005 149.844 499.641 149.844C438.441 149.844 420.693 187.788 417.633 233.688ZM622.452 416.676L747.912 254.496L631.02 103.332H706.296L788.916 217.164L872.76 103.332H946.2L829.92 254.496L955.38 416.676H876.432L789.528 290.604L702.012 416.676H622.452Z" fill="currentColor"/>
    <path d="M1195.22 21.3423C1199.01 21.3424 1202.55 23.2768 1205.81 26.6265C1209.06 29.9743 1212.14 34.8402 1215.03 40.9858C1220.81 53.2843 1225.98 70.9957 1230.31 92.7749C1236.49 123.842 1240.99 163.329 1243.06 207.497C1245.04 168.74 1249.06 134.088 1254.51 106.604C1258.48 86.5946 1263.23 70.315 1268.53 59.0063C1271.18 53.3557 1274 48.8741 1277 45.7866C1279.99 42.6974 1283.27 40.8979 1286.78 40.8979C1290.29 40.8981 1293.56 42.6975 1296.56 45.7866C1299.55 48.8741 1302.37 53.3556 1305.03 59.0063C1310.33 70.315 1315.07 86.5945 1319.04 106.604C1324.73 135.304 1328.87 171.821 1330.75 212.666C1332.39 186.451 1335.28 162.981 1339.08 144C1342.12 128.775 1345.76 116.368 1349.84 107.735C1351.88 103.422 1354.07 99.9788 1356.4 97.5962C1358.73 95.2117 1361.31 93.7866 1364.11 93.7866C1366.91 93.7866 1369.5 95.2116 1371.83 97.5962C1374.16 99.9788 1376.34 103.422 1378.38 107.735C1382.46 116.368 1386.1 128.775 1389.15 144C1393.72 166.881 1396.98 196.283 1398.32 229.154C1399.23 221.906 1400.42 215.317 1401.84 209.621C1403.54 202.788 1405.59 197.165 1407.92 193.217C1410.19 189.36 1412.99 186.675 1416.33 186.675C1419.68 186.676 1422.47 189.36 1424.75 193.217C1427.07 197.165 1429.12 202.788 1430.83 209.621C1434.24 223.306 1436.33 242.142 1436.33 262.898C1436.33 283.654 1434.24 302.489 1430.83 316.174C1429.12 323.007 1427.07 328.631 1424.75 332.579C1422.47 336.436 1419.68 339.119 1416.33 339.12C1412.99 339.12 1410.19 336.436 1407.92 332.579C1405.59 328.631 1403.54 323.007 1401.84 316.174C1400.42 310.478 1399.23 303.889 1398.32 296.641C1396.98 329.512 1393.72 358.915 1389.15 381.796C1386.1 397.021 1382.46 409.428 1378.38 418.061C1376.34 422.374 1374.16 425.816 1371.83 428.199C1369.5 430.583 1366.91 432.009 1364.11 432.009C1361.31 432.009 1358.73 430.584 1356.4 428.199C1354.07 425.816 1351.88 422.374 1349.84 418.061C1345.76 409.428 1342.12 397.021 1339.08 381.796C1335.28 362.814 1332.39 339.344 1330.75 313.129C1328.87 353.974 1324.73 390.492 1319.04 419.192C1315.07 439.201 1310.33 455.481 1305.03 466.79C1302.37 472.44 1299.55 476.922 1296.56 480.009C1293.56 483.098 1290.29 484.898 1286.78 484.898C1283.27 484.898 1279.99 483.098 1277 480.009C1274 476.922 1271.18 472.44 1268.53 466.79C1263.23 455.481 1258.48 439.201 1254.51 419.192C1249.06 391.708 1245.04 357.055 1243.06 318.298C1240.99 362.466 1236.49 401.953 1230.31 433.021C1225.98 454.8 1220.81 472.512 1215.03 484.81C1212.14 490.956 1209.06 495.822 1205.81 499.169C1202.55 502.519 1199.01 504.454 1195.22 504.454C1191.44 504.454 1187.89 502.519 1184.64 499.169C1181.38 495.822 1178.31 490.956 1175.42 484.81C1169.64 472.512 1164.46 454.8 1160.13 433.021C1153.95 401.956 1149.46 362.472 1147.39 318.308C1145.4 357.061 1141.38 391.71 1135.93 419.192C1131.96 439.202 1127.22 455.481 1121.91 466.79C1119.26 472.44 1116.44 476.922 1113.45 480.009C1110.45 483.098 1107.18 484.898 1103.67 484.898C1100.16 484.898 1096.88 483.098 1093.89 480.009C1090.89 476.922 1088.07 472.44 1085.42 466.79C1080.12 455.481 1075.37 439.202 1071.4 419.192C1065.71 390.491 1061.58 353.973 1059.69 313.128C1058.05 339.344 1055.16 362.814 1051.37 381.796C1048.32 397.021 1044.68 409.428 1040.6 418.061C1038.56 422.374 1036.38 425.816 1034.05 428.199C1031.72 430.583 1029.14 432.009 1026.33 432.009C1023.53 432.009 1020.95 430.584 1018.62 428.199C1016.29 425.816 1014.11 422.374 1012.07 418.061C1007.99 409.428 1004.34 397.021 1001.3 381.796C996.723 358.916 993.462 329.514 992.122 296.645C991.214 303.891 990.024 310.479 988.604 316.174C986.901 323.007 984.852 328.631 982.525 332.579C980.252 336.436 977.453 339.12 974.111 339.12C970.77 339.12 967.97 336.436 965.697 332.579C963.371 328.631 961.321 323.007 959.618 316.174C956.207 302.489 954.111 283.654 954.111 262.898C954.111 242.142 956.207 223.306 959.618 209.621C961.321 202.788 963.371 197.165 965.697 193.217C967.97 189.36 970.77 186.675 974.111 186.675C977.453 186.675 980.252 189.36 982.525 193.217C984.852 197.165 986.901 202.788 988.604 209.621C990.024 215.316 991.214 221.904 992.122 229.15C993.462 196.281 996.723 166.88 1001.3 144C1004.34 128.775 1007.99 116.368 1012.07 107.735C1014.11 103.422 1016.29 99.9787 1018.62 97.5962C1020.95 95.2115 1023.53 93.7866 1026.33 93.7866C1029.14 93.7868 1031.72 95.2116 1034.05 97.5962C1036.38 99.9787 1038.56 103.422 1040.6 107.735C1044.68 116.368 1048.32 128.775 1051.37 144C1055.16 162.981 1058.05 186.451 1059.69 212.666C1061.58 171.821 1065.71 135.304 1071.4 106.604C1075.37 86.5944 1080.12 70.315 1085.42 59.0063C1088.07 53.3558 1090.89 48.8741 1093.89 45.7866C1096.88 42.6974 1100.16 40.8979 1103.67 40.8979C1107.18 40.898 1110.45 42.6975 1113.45 45.7866C1116.44 48.8741 1119.26 53.3558 1121.91 59.0063C1127.22 70.315 1131.96 86.5945 1135.93 106.604C1141.38 134.086 1145.4 168.735 1147.39 207.487C1149.46 163.323 1153.95 123.84 1160.13 92.7749C1164.46 70.9957 1169.64 53.2843 1175.42 40.9858C1178.31 34.8403 1181.38 29.9743 1184.64 26.6265C1187.89 23.2769 1191.44 21.3423 1195.22 21.3423Z" fill="url(#nexorChatBrandGrad)"/>
    <path d="M1490.82 416.676V103.332H1525.71L1541.01 146.784C1559.98 106.392 1596.7 95.3758 1652.39 99.0477V155.964C1588.13 146.784 1556.31 177.996 1556.31 251.436V416.676H1490.82Z" fill="currentColor"/>
    <path d="M1681.68 78.9118C1650.58 78.9118 1642.32 61.0558 1642.32 39.3598C1642.32 17.9518 1650.86 -0.000219345 1681.68 -0.000219345C1712.88 -0.000219345 1721.04 17.9518 1721.04 39.4558C1721.04 61.0558 1712.88 78.9118 1681.68 78.9118ZM1651.82 39.4558C1651.82 57.3118 1658.26 70.7518 1681.68 70.7518C1705.2 70.7518 1711.54 57.3118 1711.54 39.4558C1711.54 21.5998 1705.2 8.15978 1681.68 8.15978C1658.35 8.15978 1651.82 21.6958 1651.82 39.4558ZM1662.1 39.8398C1662.1 28.2238 1666.9 18.3358 1681.87 18.3358C1693.87 18.3358 1700.11 25.0558 1700.11 36.1918H1691.28C1690.7 29.2798 1687.82 26.3998 1681.78 26.3998C1674.1 26.3998 1671.02 31.2958 1671.02 39.5518C1671.02 47.9038 1674.1 52.8958 1681.78 52.8958C1687.82 52.8958 1690.8 50.0158 1691.28 43.3918H1700.11C1700.11 54.6238 1694.16 61.0558 1682.16 61.0558C1666.9 61.0558 1662.1 51.2638 1662.1 39.8398Z" fill="currentColor"/>
  </svg>`;

function buildFooter(cfg: ResolvedCfg): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "nexor-chat__footer";
  // Brand attribution only — not a link (intentionally not clickable). A custom
  // CSS tooltip (instant, reliable — unlike a native SVG <title>) surfaces the
  // site on hover so visitors know where to find Nexor.
  el.innerHTML = `<span>${cfg.aria.poweredBy}</span><a class="nexor-chat__brand" href="https://getnexor.ai?utm_source=webchat-widget" target="_blank" rel="noopener" aria-label="Nexor"><span class="nexor-chat__brand-tip" role="tooltip">getnexor.ai</span>${NEXOR_WORDMARK}</a>`;
  return el;
}

// ─── Message bubbles ───────────────────────────────────────────────────────

export type MessageRole = "user" | "bot" | "system";

/**
 * Build a chat bubble. `textContent` is used (never innerHTML) so user input
 * is rendered as plain text — no XSS surface from message payloads.
 */
export function buildMessage(
  role: MessageRole,
  text: string,
  ts?: number,
  locale?: string,
): HTMLDivElement {
  const el = document.createElement("div");
  el.className = `nexor-chat__msg nexor-chat__msg--${role}`;
  const body = document.createElement("span");
  body.className = "nexor-chat__msg-text";
  appendLinkified(body, text);
  el.appendChild(body);
  // Relative time under user/bot messages (not system notices). `data-ts` lets
  // the widget refresh the label as time passes (see index.ts).
  if (role !== "system" && ts) {
    const time = document.createElement("time");
    time.className = "nexor-chat__msg-time";
    time.dataset.ts = String(ts);
    time.textContent = formatRelativeTime(ts, locale);
    el.appendChild(time);
  }
  return el;
}

/**
 * Append `text` to `parent`, turning URLs into real <a> nodes. Everything is
 * added via text nodes / createElement (never innerHTML), so message payloads
 * carry no XSS surface — same guarantee as the old `textContent` path.
 * Matching rules live in `tokenizeLinks` (linkify.ts), unit-tested in isolation.
 */
function appendLinkified(parent: HTMLElement, text: string): void {
  for (const tok of tokenizeLinks(text)) {
    if (tok.type === "text") {
      parent.appendChild(document.createTextNode(tok.value));
      continue;
    }
    const a = document.createElement("a");
    a.className = "nexor-chat__msg-link";
    a.href = tok.href as string;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = tok.value;
    parent.appendChild(a);
  }
}

/**
 * A bot message whose body is a File Library attachment. Images render inline
 * (the image IS the bubble — rounded, no text padding); documents render as a
 * download link. The URL comes verbatim from the API's resolved `media` item;
 * an unreachable image collapses to nothing rather than a broken-image icon.
 */
export function buildMediaMessage(item: ChatMedia, downloadLabel = "Download file"): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "nexor-chat__msg nexor-chat__msg--bot nexor-chat__msg--media";
  if (item.type === "image") {
    const img = document.createElement("img");
    img.className = "nexor-chat__media-img";
    img.src = item.url;
    img.alt = item.filename || "";
    img.loading = "lazy";
    img.addEventListener("error", () => el.remove());
    el.appendChild(img);
  } else {
    const a = document.createElement("a");
    a.className = "nexor-chat__media-doc";
    a.href = item.url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = item.filename || downloadLabel;
    el.appendChild(a);
  }
  return el;
}

/**
 * Relative within the last week ("ahora", "hace 3 minutos", "hace 2 horas",
 * "ayer"), then an absolute date ("12 may"). Localized via Intl; falls back to
 * a plain clock time if Intl.RelativeTimeFormat is unavailable.
 */
export function formatRelativeTime(
  ts: number,
  locale?: string,
  now: number = Date.now(),
): string {
  try {
    const diff = Math.max(0, now - ts);
    const min = Math.round(diff / 60000);
    const hr = Math.round(diff / 3600000);
    const day = Math.round(diff / 86400000);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (diff < 45000) return rtf.format(0, "second"); // "ahora" / "now"
    if (min < 60) return rtf.format(-min, "minute");
    if (hr < 24) return rtf.format(-hr, "hour");
    if (day < 7) return rtf.format(-day, "day"); // includes "ayer"/"yesterday"
    return new Date(ts).toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
    });
  } catch {
    try {
      return new Date(ts).toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  }
}

// ─── Resume banner (returning visitor) ──────────────────────────────────────

export interface ResumeBannerRefs {
  el: HTMLDivElement;
  resetBtn: HTMLButtonElement;
}

/**
 * A slim banner shown at the top of a restored conversation: it tells the
 * visitor we kept their chat (with their name/email when known) and offers a
 * "start over" reset. Replaces the broken state where the capture form rendered
 * on top of an existing conversation.
 */
export function buildResumeBanner(
  text: string,
  resetLabel: string,
): ResumeBannerRefs {
  const el = document.createElement("div");
  el.className = "nexor-chat__resume";

  const label = document.createElement("span");
  label.className = "nexor-chat__resume-text";
  label.textContent = text;

  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className = "nexor-chat__resume-reset";
  resetBtn.textContent = resetLabel;

  el.appendChild(label);
  el.appendChild(resetBtn);
  return { el, resetBtn };
}

// ─── Suggested-question chips ───────────────────────────────────────────────

export interface SuggestionsRefs {
  el: HTMLDivElement;
  buttons: HTMLButtonElement[];
}

/**
 * Outline chips rendered under the greeting: each is a "candidate first
 * message" from the visitor (right-aligned, like user bubbles). textContent
 * only — same XSS rule as buildMessage.
 */
export function buildSuggestions(items: string[]): SuggestionsRefs {
  const el = document.createElement("div");
  el.className = "nexor-chat__suggestions";
  const buttons = items.map((text) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "nexor-chat__suggestion";
    b.textContent = text;
    el.appendChild(b);
    return b;
  });
  return { el, buttons };
}

/** Three-dot typing indicator. Animation lives in styles.ts. */
export function buildTypingIndicator(): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "nexor-chat__typing";
  el.innerHTML = "<span></span><span></span><span></span>";
  return el;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/** "Tienda Demo" → "TD"; single word → its first letter. */
function initialsOf(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

function makeInput(
  name: string,
  placeholder: string,
  type: string,
  value?: string,
): HTMLInputElement {
  const i = document.createElement("input");
  i.name = name;
  i.type = type;
  i.placeholder = placeholder;
  const max = FIELD_MAX_LENGTH[name];
  if (max) i.maxLength = max;
  if (value) i.value = value;
  return i;
}

/**
 * Escape strings before interpolation into innerHTML. Used only for
 * SDK-controlled config values (title, subtitle) — user-typed messages
 * always go through textContent, which doesn't need escaping.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
